'use client';

import { useEffect, useRef } from 'react';

// Improved version of https://usehooks.com/useOnClickOutside/
const useClickOutside = (ref, handler) => {
  // Keep the latest handler in a ref, updated by its own effect, so the listener
  // subscription effect below depends only on `ref`. It previously depended on
  // [ref, handler] as well: any change in the caller's handler identity between
  // renders re-ran the subscription effect, which re-created (and thus reset)
  // the mousedown/click pairing state (startedInside/startedWhenMounted). If a
  // re-render happened between a user's mousedown and click (e.g. a blur handler
  // elsewhere setting state), the freshly re-subscribed listener would see
  // startedWhenMounted=false and silently swallow that click — requiring several
  // more clicks before one landed on a listener that had lived through both
  // mousedown and click.
  const savedHandler = useRef(handler);
  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    let startedInside = false;
    let startedWhenMounted = false;

    const listener = (event) => {
      // Do nothing if `mousedown` or `touchstart` started inside ref element
      if (startedInside || !startedWhenMounted) return;
      // Do nothing if clicking ref's element or descendent elements
      if (!ref.current || ref.current.contains(event.target)) return;

      savedHandler.current(event);
    };

    const validateEventStart = (event) => {
      startedWhenMounted = ref.current;
      startedInside = ref.current && ref.current.contains(event.target);
    };

    document.addEventListener('mousedown', validateEventStart);
    document.addEventListener('touchstart', validateEventStart);
    document.addEventListener('click', listener);

    return () => {
      document.removeEventListener('mousedown', validateEventStart);
      document.removeEventListener('touchstart', validateEventStart);
      document.removeEventListener('click', listener);
    };
  }, [ref]);
};

export default useClickOutside;
